import React, {
  Fragment,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useAsyncFn } from "react-use";
import { SearchDateTimeRange } from "@/api/swagger";
import { AxiosResponse } from "axios";
import useMediaQuery from "@/hooks/useMediaQuery";
import { PeriodDataPicker, PeriodSearchHandler } from "./DatePicker";
import classNames from "classnames";
import Flexbox from "../General/Flexbox";
import ConditionalFragment from "../General/ConditionalFragment";
import { isSet } from "@/tools/format.checker";
import { getDateTimeRangePayload } from "@/helper/dateHelper";
import { hungerMoment } from "@/moment";

export type AnalysisWrapperSearchApi<Data> = (config: {
  time: SearchDateTimeRange;
}) => Promise<AxiosResponse<Array<Data>>>;

interface Props<Data> {
  title?: string;
  searchApi: AnalysisWrapperSearchApi<Data>;
  chart: React.FC<{ data: Array<Data>; xTickInterval: number | undefined }>;
  unit: string;
  legends: Array<{ color: string; label: string }>;
}

interface AnalysisWrapperComponents extends CustomizeFunctionComponent {
  <Data>(props: PropsWithChildren<Props<Data>>): ReturnType<React.FC>;
}

const AnalysisWrapper: AnalysisWrapperComponents = ({
  searchApi,
  title,
  chart,
  unit,
  legends,
}) => {
  const defaultStartAt = useRef(hungerMoment().startOf("month"));
  const defaultEndAt = useRef(hungerMoment());

  const match = useMediaQuery("(min-width: 472px)");

  const [{ value: summaries = [] }, fetcher] = useAsyncFn(
    async (config: { time: SearchDateTimeRange }) => {
      const { data } = await searchApi(config);
      return data;
    },
    [searchApi]
  );

  const onSearch = useCallback<PeriodSearchHandler>(
    ({ startAt, endAt }) => {
      fetcher({
        time: getDateTimeRangePayload(startAt, endAt),
      });
    },
    [fetcher]
  );

  useEffect(() => {
    fetcher({
      time: getDateTimeRangePayload(
        defaultStartAt.current,
        defaultEndAt.current
      ),
    });
  }, [fetcher]);

  const xTickInterval = useMemo(() => {
    const length = summaries.length;
    if (!match) return 4;
    if (length < 10) return undefined;
    return Math.round(length / 10);
  }, [summaries, match]);

  return (
    <Fragment>
      <Flexbox
        direction={"column"}
        className={classNames("px-10 py-5", "mb-5", "bg-white rounded-xl")}
      >
        <ConditionalFragment condition={isSet(title)}>
          <p
            className={classNames(
              "mr-auto mb-4",
              "text-2xl text-primary-pink font-semibold"
            )}
          >
            {title}
          </p>
        </ConditionalFragment>

        <PeriodDataPicker
          onSearch={onSearch}
          defaultPeriod={{
            startAt: defaultStartAt.current,
            endAt: defaultEndAt.current,
          }}
        />
      </Flexbox>

      <div className={"p-5 md:pt-5 md:pb-9 md:px-12 rounded-xl bg-white"}>
        <div className={"flex flex-col md:flex-row md:justify-between mb-6 "}>
          <p className={"mb-2 md:mb-0 text-sm text-grey-3"}>單位：{unit}</p>
          <div className={"flex items-center"}>
            {legends.map(({ color, label }) => (
              <div key={color} className={"flex items-center mr-5"}>
                <div
                  style={{ backgroundColor: color }}
                  className={"mr-2 w-4 h-4 rounded"}
                />
                <p className={"text-grey-2 text-sm"}>{label}</p>
              </div>
            ))}
          </div>
        </div>
        <div className={"px-8 w-full relative"}>
          <div
            className={"w-full h-[472px] md:h-auto"}
            style={{ paddingTop: `${(463 / 918) * 100}%` }}
          />
          <div className={"absolute top-0 left-0 w-full h-[472px] md:h-full"}>
            {React.createElement(chart, {
              data: summaries,
              xTickInterval,
            })}
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default AnalysisWrapper;
